import type { CSSProperties, ReactNode } from 'react';
import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';

const RADIUS = '22px';

/** Tune glow strength + motion in one place (intensity 0–2, speed 0.5–2). */
export type AmbientGlowOptions = {
  /** 0 = off, 1 = default, ~1.6 = stronger halo */
  intensity?: number;
  /** 1 = default; lower = slower drift */
  speed?: number;
  /** Set false to freeze the halo */
  animate?: boolean;
};

export const EMPLOYER_REVIEW_GLOW: Required<AmbientGlowOptions> = {
  intensity: 1.15,
  speed: 1,
  animate: true,
};

/** Subtle halo for decision summary — opaque reading surface, no backdrop blur. */
export const EMPLOYER_SUMMARY_GLOW: Required<AmbientGlowOptions> = {
  intensity: 0.35,
  speed: 1,
  animate: true,
};

/** PDL adjudicator brief — static halo only (no drift on consequential surfaces). */
export const PDL_DECISION_SUMMARY_GLOW: Required<AmbientGlowOptions> = {
  intensity: 0.2,
  speed: 1,
  animate: false,
};

export type AmbientSurfaceMode = 'summary' | 'chrome';

function clampGlow(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function glowVars(options: Required<AmbientGlowOptions>): CSSProperties {
  const intensity = clampGlow(options.intensity, 0, 2);
  const speed = clampGlow(options.speed, 0.4, 2.5);
  return {
    ['--ambient-intensity' as string]: String(intensity),
    ['--ambient-drift-a' as string]: `${14 / speed}s`,
    ['--ambient-drift-b' as string]: `${18 / speed}s`,
    ['--ambient-shift' as string]: `${16 / speed}s`,
    ['--ambient-breathe' as string]: `${11 / speed}s`,
  };
}

const AMBIENT_KEYFRAMES = {
  '@keyframes employerAuroraDriftA': {
    '0%': { transform: 'translate3d(-4%, -3%, 0) scale(1.04)' },
    '50%': { transform: 'translate3d(4%, 2%, 0) scale(1.08)' },
    '100%': { transform: 'translate3d(-2%, 3%, 0) scale(1.05)' },
  },
  '@keyframes employerAuroraDriftB': {
    '0%': { transform: 'translate3d(3%, 2%, 0) scale(1.06)' },
    '50%': { transform: 'translate3d(-3%, -2%, 0) scale(1.03)' },
    '100%': { transform: 'translate3d(2%, -3%, 0) scale(1.07)' },
  },
  '@keyframes employerAuroraShift': {
    '0%': { backgroundPosition: '0% 30%, 100% 70%, 60% 10%, 20% 90%' },
    '50%': { backgroundPosition: '100% 70%, 0% 30%, 30% 90%, 80% 10%' },
    '100%': { backgroundPosition: '0% 30%, 100% 70%, 60% 10%, 20% 90%' },
  },
  '@keyframes employerAuroraBreathe': {
    '0%, 100%': { opacity: 'calc(0.55 * var(--ambient-intensity, 1))' },
    '50%': { opacity: 'calc(0.78 * var(--ambient-intensity, 1))' },
  },
} as const;

function auroraBackground(isLight: boolean, layer: 'a' | 'b'): string {
  const amber = isLight ? '255, 188, 108' : '255, 168, 88';
  const lavender = isLight ? '188, 158, 238' : '158, 128, 218';
  const blue = isLight ? '118, 172, 255' : '88, 142, 255';
  const cyan = isLight ? '118, 222, 245' : '78, 198, 228';

  if (layer === 'a') {
    return `
      radial-gradient(ellipse 70% 55% at 8% 15%, rgba(${amber}, 0.62) 0%, transparent 72%),
      radial-gradient(ellipse 65% 50% at 92% 85%, rgba(${lavender}, 0.52) 0%, transparent 70%)
    `;
  }
  return `
    radial-gradient(ellipse 60% 50% at 82% 10%, rgba(${blue}, 0.48) 0%, transparent 68%),
    radial-gradient(ellipse 55% 48% at 18% 92%, rgba(${cyan}, 0.42) 0%, transparent 66%)
  `;
}

/** Opaque premium card for dense adjudication copy — deterministic contrast. */
function summarySurfaceSx(theme: Theme) {
  const isLight = theme.palette.mode === 'light';
  const solid = isLight ? '#fafbfc' : theme.palette.background.paper;
  return {
    position: 'relative' as const,
    zIndex: 2,
    borderRadius: RADIUS,
    color: theme.palette.text.primary,
    bgcolor: solid,
    border: '1px solid',
    borderColor: isLight ? alpha(theme.palette.divider, 0.95) : alpha(theme.palette.divider, 0.55),
    boxShadow: isLight
      ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.95)}, 0 4px 18px ${alpha(theme.palette.common.black, 0.05)}`
      : `0 6px 24px ${alpha(theme.palette.common.black, 0.28)}`,
    overflow: 'hidden',
    '@media (prefers-reduced-transparency: reduce)': {
      bgcolor: solid,
    },
    '@media (prefers-contrast: more)': {
      borderColor: theme.palette.divider,
      boxShadow: 'none',
    },
  };
}

function glassSurfaceSx(theme: Theme, intensity: number) {
  const isLight = theme.palette.mode === 'light';
  const solidFallback = isLight ? '#fafbfc' : theme.palette.background.paper;
  const i = clampGlow(intensity, 0, 2);
  const halo = (a: number) => a * i;

  return {
    position: 'relative' as const,
    zIndex: 2,
    borderRadius: RADIUS,
    color: theme.palette.text.primary,
    background: isLight ? 'rgba(255, 255, 255, 0.78)' : 'rgba(22, 21, 38, 0.86)',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
    border: isLight ? '1px solid rgba(255, 255, 255, 0.55)' : '1px solid rgba(255, 255, 255, 0.06)',
    boxShadow: isLight
      ? `
        inset 0 1px 0 rgba(255, 255, 255, 0.9),
        0 10px 36px rgba(255, 188, 108, ${halo(0.12)}),
        0 14px 48px rgba(188, 158, 238, ${halo(0.1)}),
        0 8px 28px rgba(118, 172, 255, ${halo(0.08)}),
        0 4px 16px rgba(15, 23, 42, 0.04)
      `
      : `
        inset 0 1px 0 rgba(255, 255, 255, 0.05),
        0 12px 40px rgba(88, 142, 255, ${halo(0.16)}),
        0 16px 52px rgba(158, 128, 218, ${halo(0.12)}),
        0 8px 30px rgba(0, 0, 0, 0.32)
      `,
    overflow: 'hidden',
    transition: 'box-shadow 0.6s ease, background 0.6s ease',
    '@media (prefers-reduced-transparency: reduce)': {
      background: solidFallback,
      WebkitBackdropFilter: 'none',
      backdropFilter: 'none',
    },
    '@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px)))': {
      background: solidFallback,
      WebkitBackdropFilter: 'none',
      backdropFilter: 'none',
    },
    '.employer-ambient-glass-shell:hover &': {
      boxShadow: isLight
        ? `
          inset 0 1px 0 rgba(255, 255, 255, 0.95),
          0 12px 42px rgba(255, 188, 108, ${halo(0.16)}),
          0 18px 56px rgba(188, 158, 238, ${halo(0.13)}),
          0 10px 32px rgba(118, 172, 255, ${halo(0.1)}),
          0 6px 20px rgba(15, 23, 42, 0.05)
        `
        : `
          inset 0 1px 0 rgba(255, 255, 255, 0.07),
          0 14px 46px rgba(88, 142, 255, ${halo(0.2)}),
          0 20px 58px rgba(158, 128, 218, ${halo(0.15)}),
          0 10px 34px rgba(0, 0, 0, 0.38)
        `,
    },
  };
}

/**
 * MUI Paper `outlined` forces solid white — must override so glass + glow show through.
 */
export const ambientGlassInnerPaperSx: SxProps<Theme> = {
  position: 'relative',
  zIndex: 1,
  bgcolor: 'transparent !important',
  backgroundColor: 'transparent !important',
  borderRadius: RADIUS,
  border: 'none !important',
  boxShadow: 'none !important',
  backgroundImage: 'none !important',
  overflow: 'visible',
  '&.MuiPaper-root': {
    bgcolor: 'transparent !important',
    backgroundColor: 'transparent !important',
    boxShadow: 'none !important',
  },
  '&.MuiPaper-outlined': {
    border: 'none !important',
    bgcolor: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
};

/** Theme-aware link colors for glass card actions. */
export const ambientGlassLinkSx: SxProps<Theme> = (theme) => ({
  color: `${theme.palette.mode === 'light' ? '#0E7490' : '#5EEAD4'} !important`,
  '&:hover': {
    color: `${theme.palette.mode === 'light' ? '#0891B2' : '#99F6E4'} !important`,
  },
});

/**
 * Soft ambient aurora behind the card — no hard piped border ring.
 * Adjust via `glow` prop or `EMPLOYER_REVIEW_GLOW` defaults.
 */
export function AmbientGlassShell({
  children,
  glow = EMPLOYER_REVIEW_GLOW,
  surface = 'chrome',
}: {
  children: ReactNode;
  glow?: AmbientGlowOptions;
  /** summary = opaque decision card; chrome = translucent overlay material */
  surface?: AmbientSurfaceMode;
}) {
  const resolved: Required<AmbientGlowOptions> = {
    intensity: glow.intensity ?? EMPLOYER_REVIEW_GLOW.intensity,
    speed: glow.speed ?? EMPLOYER_REVIEW_GLOW.speed,
    animate: glow.animate ?? EMPLOYER_REVIEW_GLOW.animate,
  };
  const intensity = clampGlow(resolved.intensity, 0, 2);
  const animate = resolved.animate && intensity > 0;
  const isSummary = surface === 'summary';

  return (
    <Box
      className="employer-ambient-glass-shell"
      style={glowVars(resolved)}
      sx={[
        AMBIENT_KEYFRAMES,
        {
          position: 'relative',
          borderRadius: RADIUS,
          mx: 0.5,
          my: 1.5,
          overflow: 'visible',
          '@media (prefers-reduced-motion: reduce)': {
            '& .employer-aurora-layer-a, & .employer-aurora-layer-b': {
              animation: 'none !important',
            },
          },
          '@media (prefers-reduced-transparency: reduce)': {
            '& .employer-aurora-layer-a, & .employer-aurora-layer-b': {
              display: 'none',
            },
          },
        },
      ]}
    >
      {intensity > 0 ? (
        <>
          <Box
            className="employer-aurora-layer-a"
            aria-hidden
            sx={(theme) => ({
              position: 'absolute',
              inset: -32,
              zIndex: 0,
              pointerEvents: 'none',
              borderRadius: RADIUS,
              opacity: `calc(${theme.palette.mode === 'light' ? 0.72 : 0.62} * var(--ambient-intensity, 1))`,
              filter: 'blur(52px)',
              background: auroraBackground(theme.palette.mode === 'light', 'a'),
              backgroundSize: '220% 220%, 220% 220%',
              animation: animate
                ? `
                  employerAuroraDriftA var(--ambient-drift-a, 14s) ease-in-out infinite alternate,
                  employerAuroraShift var(--ambient-shift, 16s) ease-in-out infinite,
                  employerAuroraBreathe var(--ambient-breathe, 11s) ease-in-out infinite
                `
                : 'none',
            })}
          />
          <Box
            className="employer-aurora-layer-b"
            aria-hidden
            sx={(theme) => ({
              position: 'absolute',
              inset: -28,
              zIndex: 0,
              pointerEvents: 'none',
              borderRadius: RADIUS,
              opacity: `calc(${theme.palette.mode === 'light' ? 0.58 : 0.5} * var(--ambient-intensity, 1))`,
              filter: 'blur(44px)',
              background: auroraBackground(theme.palette.mode === 'light', 'b'),
              backgroundSize: '200% 200%, 200% 200%',
              animation: animate
                ? `
                  employerAuroraDriftB var(--ambient-drift-b, 18s) ease-in-out infinite alternate-reverse,
                  employerAuroraShift calc(var(--ambient-shift, 16s) * 1.2) ease-in-out infinite reverse
                `
                : 'none',
            })}
          />
        </>
      ) : null}

      <Box sx={(theme) => (isSummary ? summarySurfaceSx(theme) : glassSurfaceSx(theme, intensity))}>
        {children}
      </Box>
    </Box>
  );
}
